import { ReactNode, useEffect, useState } from "react";
import { Calendar } from "react-multi-date-picker";
import { Box, Popover, TextField } from "../../components";
import { getDelimitedString } from "../../services";
import { ValidationErrors } from "../../hooks";
import { displayErrorMessages, getErrorMessages } from "./input-functions";
import { parseDateInput } from "./date-input";

export const MultiDatePicker = (props: {
  value?: Date[] | undefined;
  onChange?: (selectedDates: Date[] | undefined) => void;
  label?: ReactNode;
  required?: boolean;
  errors?: ValidationErrors | string[];
}) => {
  const DATE_FORMAT: string = "MM/DD/YYYY";
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [inputValue, setInputValue] = useState<string>("");

  const errorMsg = getErrorMessages(props.errors);
  const hasError = errorMsg?.length > 0 ? true : false;

  const handleChange = (values) => {
    if (values && props.onChange) {
      props.onChange(values.map((x) => x.format(DATE_FORMAT)));
    }

    return null;
  };

  useEffect(() => {
    setInputValue(
      props.value
        ? getDelimitedString(
            props.value.map((x) => x.toString()),
            ", "
          ) || ""
        : ""
    );
  }, [props.value]);

  const parseDateList = (value: string) => {
    const parseParts = (parts: string[], startIndex = 0): string[] | undefined => {
      if (startIndex === parts.length) return [];

      for (let endIndex = startIndex + 1; endIndex <= parts.length; endIndex++) {
        const date = parseDateInput(parts.slice(startIndex, endIndex).join(","));
        if (!date.isValid()) continue;

        const remainingDates = parseParts(parts, endIndex);
        if (remainingDates) return [date.format(DATE_FORMAT), ...remainingDates];
      }

      return undefined;
    };

    return parseParts(value.split(/[;,\n]/));
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);

    if (!value.trim()) {
      props.onChange?.([]);
      return;
    }

    const parsedDates = parseDateList(value);
    if (parsedDates) props.onChange?.(parsedDates as any);
  };

  return (
    <Box>
      <TextField
        label={props.label}
        fullWidth
        required={props.required}
        error={hasError}
        helperText={displayErrorMessages(errorMsg)}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        autoComplete="off"
        value={inputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        multiline
      />
      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Calendar
          multiple
          value={props.value}
          onChange={handleChange}
          format={DATE_FORMAT}
          // format="Date:MM/DD/YYYY, Time:HH:mm:ss"
          // formattingIgnoreList={["Date", "Time"]}
        />
      </Popover>
    </Box>
  );
};
